// SettingsPanel — slide-in from the right of the call modal. Three tabs:
//
//   Audio    — Mic + Speaker picker (re-uses devices from useMediaDevices),
//              Noise Suppression toggle (Stream's Krisp integration), and
//              an input-level meter that runs as long as the panel is open.
//
//   Video    — Camera picker, Background-Blur toggle (Phase 2 hook reused).
//
//   General  — Keyboard shortcuts (informational only) + a copy of the
//              encryption notice for transparency.
//
// The slide-in animation is plain CSS keyframes in call.css.

import React, { useState } from "react";
import { useBackgroundFilters } from "@stream-io/video-react-sdk";

import { TP, I, Icon } from "./shared";
import { useMicLevel } from "../../../hooks/inbox/useMediaDevices";

// Light variant of the chrome close button — used by the settings
// panel header on the white surface.
const lightChromeBtn = {
  background: "#FFFFFF",
  border: "1px solid #E5E7EB",
  color: "#6B7280",
  width: 28,
  height: 28,
  borderRadius: 8,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
};

export default function SettingsPanel({
  onClose,
  devices,
  onSelectMic,
  onSelectSpeaker,
  onSelectCamera,
  isVideo,
}) {
  const [tab, setTab] = useState("audio");
  // Live mic-level while the panel is open — gives the user visible
  // confirmation that the selected mic is actually picking up audio.
  const micLevel = useMicLevel(true);

  return (
    <div
      className="tp-settings-panel"
      role="dialog"
      aria-label="Call settings"
      style={{
        position: "absolute",
        top: 0,
        right: 0,
        bottom: 0,
        width: 320,
        maxWidth: "100%",
        background: "#FFFFFF",
        borderLeft: `1px solid ${TP.border}`,
        boxShadow: "-12px 0 32px rgba(20,40,80,0.08)",
        display: "flex",
        flexDirection: "column",
        zIndex: 25,
        color: TP.text,
      }}
    >
      <div
        style={{
          padding: "16px 18px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: `1px solid ${TP.border}`,
        }}
      >
        <div style={{ fontSize: 14, fontWeight: 600, color: TP.text }}>
          Call settings
        </div>
        <button
          onClick={onClose}
          aria-label="Close settings"
          style={lightChromeBtn}
        >
          <Icon d={I.close} size={14} />
        </button>
      </div>

      <div
        style={{
          display: "flex",
          gap: 4,
          padding: "10px 12px",
          borderBottom: `1px solid ${TP.border}`,
        }}
      >
        {[
          { id: "audio", label: "Audio" },
          // Hide the Video tab on audio-only calls — nothing to show.
          ...(isVideo ? [{ id: "video", label: "Video" }] : []),
          { id: "general", label: "General" },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              flex: 1,
              padding: "7px 0",
              border: "none",
              cursor: "pointer",
              fontSize: 12,
              fontWeight: 600,
              background: tab === t.id ? "#EBF3FF" : "transparent",
              color: tab === t.id ? TP.blue : TP.text2,
              borderRadius: 8,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div
        style={{
          flex: 1,
          overflow: "auto",
          padding: 16,
          display: "flex",
          flexDirection: "column",
          gap: 18,
        }}
      >
        {tab === "audio" && (
          <AudioTab
            devices={devices}
            onSelectMic={onSelectMic}
            onSelectSpeaker={onSelectSpeaker}
            micLevel={micLevel}
          />
        )}
        {tab === "video" && isVideo && (
          <VideoTab devices={devices} onSelectCamera={onSelectCamera} />
        )}
        {tab === "general" && <GeneralTab />}
      </div>
    </div>
  );
}

// — Field + Select helpers ——————————————————————————————————————————————

const Field = ({ label, children }) => (
  <div>
    <div
      style={{
        fontSize: 11,
        fontWeight: 600,
        color: TP.text2,
        marginBottom: 6,
        letterSpacing: 0.3,
        textTransform: "uppercase",
      }}
    >
      {label}
    </div>
    {children}
  </div>
);

// Native <select> for compact layout. Could be replaced by the SourceMenu
// component later if we want a fancier list with previews.
const DeviceSelect = ({ items, activeId, onChange, placeholder = "—" }) => (
  <select
    value={activeId || ""}
    onChange={(e) => onChange(e.target.value)}
    style={{
      width: "100%",
      padding: "10px 12px",
      borderRadius: 10,
      background: TP.surface,
      border: `1px solid ${TP.border}`,
      color: TP.text,
      fontSize: 13,
      fontWeight: 500,
      cursor: "pointer",
      appearance: "none",
    }}
  >
    {items.length === 0 ? (
      <option value="">{placeholder}</option>
    ) : (
      items.map((d) => (
        <option key={d.deviceId} value={d.deviceId}>
          {d.label || "Unnamed device"}
        </option>
      ))
    )}
  </select>
);

const ToggleRow = ({ label, hint, on, onChange }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
    }}
  >
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 13, fontWeight: 500 }}>{label}</div>
      {hint && (
        <div style={{ fontSize: 11, color: TP.text2, marginTop: 2 }}>
          {hint}
        </div>
      )}
    </div>
    <button
      onClick={() => onChange(!on)}
      aria-pressed={on}
      style={{
        width: 38,
        height: 22,
        borderRadius: 999,
        border: "none",
        cursor: "pointer",
        background: on ? TP.blue : "#CBD5E1",
        padding: 2,
        display: "flex",
        alignItems: "center",
        justifyContent: on ? "flex-end" : "flex-start",
        transition: "background .15s",
      }}
    >
      <span
        style={{
          width: 18,
          height: 18,
          background: "#fff",
          borderRadius: "50%",
          display: "block",
        }}
      />
    </button>
  </div>
);

// — Audio tab —————————————————————————————————————————————————————————

function AudioTab({ devices, onSelectMic, onSelectSpeaker, micLevel }) {
  return (
    <>
      <Field label="Microphone">
        <DeviceSelect
          items={devices.mic.devices}
          activeId={devices.mic.selectedDeviceId}
          onChange={onSelectMic}
          placeholder={
            devices.mic.hasBrowserPermission
              ? "No microphones"
              : "Mic permission denied"
          }
        />
      </Field>

      {devices.speaker.isSupported ? (
        <Field label="Speaker">
          <DeviceSelect
            items={devices.speaker.devices}
            activeId={devices.speaker.selectedDeviceId}
            onChange={onSelectSpeaker}
            placeholder="No output devices"
          />
        </Field>
      ) : (
        // Firefox / mobile Safari: explain why there's no speaker picker
        <div
          style={{
            fontSize: 11,
            color: TP.text3,
            padding: "8px 10px",
            background: "#F8FAFC",
            borderRadius: 8,
          }}
        >
          Speaker selection is not supported in this browser. The system
          default output will be used.
        </div>
      )}

      <Field label="Input level">
        <div
          style={{
            display: "flex",
            gap: 3,
            height: 8,
            alignItems: "center",
          }}
        >
          {Array.from({ length: 24 }).map((_, i) => {
            // Fill bars left-to-right based on the live mic level.
            // The last few bars switch from green to amber as a peak hint.
            const threshold = (i + 1) / 24;
            const lit = micLevel >= threshold;
            const color = lit
              ? threshold >= 0.78
                ? TP.warn
                : TP.ok
              : "#E5E7EB";
            return (
              <span
                key={i}
                style={{
                  flex: 1,
                  height: 6,
                  borderRadius: 2,
                  background: color,
                }}
              />
            );
          })}
        </div>
      </Field>

    </>
  );
}

// (Noise-suppression toggle removed — was a placebo control that only
// wrote to localStorage. Will be re-added once we mount Stream's
// NoiseCancellationProvider with the Krisp wasm asset.)

// — Video tab —————————————————————————————————————————————————————————

function VideoTab({ devices, onSelectCamera }) {
  const bgFilters = useBackgroundFilters();
  const blurActive = bgFilters.backgroundFilter === "blur";
  const blurReady = bgFilters.isReady;
  const blurSupported = bgFilters.isSupported;
  const handleToggleBlur = (next) => {
    if (!blurSupported || !blurReady) return;
    if (next) bgFilters.applyBackgroundBlurFilter("high");
    else bgFilters.disableBackgroundFilter();
  };

  return (
    <>
      <Field label="Camera">
        <DeviceSelect
          items={devices.camera.devices}
          activeId={devices.camera.selectedDeviceId}
          onChange={onSelectCamera}
          placeholder={
            devices.camera.hasBrowserPermission
              ? "No cameras"
              : "Camera permission denied"
          }
        />
      </Field>

      <ToggleRow
        label="Background blur"
        hint={
          !blurSupported
            ? "Not supported in this browser"
            : !blurReady
            ? "Loading model…"
            : "Blurs everything except you"
        }
        on={blurActive}
        onChange={blurSupported && blurReady ? handleToggleBlur : () => {}}
      />
    </>
  );
}

// — General tab ———————————————————————————————————————————————————————

function GeneralTab() {
  return (
    <>
      <Field label="Keyboard shortcuts">
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {[
            ["Mute / unmute", "M"],
            ["Camera on / off", "V"],
            ["End call", "⌘ ⇧ H"],
          ].map(([label, key]) => (
            <div
              key={label}
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 12,
                color: TP.text2,
              }}
            >
              <span>{label}</span>
              <kbd
                style={{
                  background: TP.surface,
                  border: `1px solid ${TP.border}`,
                  padding: "2px 6px",
                  borderRadius: 5,
                  color: TP.text,
                  fontSize: 11,
                  fontFamily: "inherit",
                }}
              >
                {key}
              </kbd>
            </div>
          ))}
        </div>
      </Field>

      <Field label="Encryption">
        <div
          style={{
            fontSize: 12,
            color: TP.text2,
            padding: "8px 10px",
            background: "rgba(16,185,129,0.08)",
            border: "1px solid rgba(16,185,129,0.2)",
            borderRadius: 8,
            lineHeight: 1.5,
          }}
        >
          This call is end-to-end encrypted between TransferPitch participants.
          Media never reaches our servers.
        </div>
      </Field>
    </>
  );
}
